import * as Collapsible from '@radix-ui/react-collapsible'
import useEmblaCarousel from 'embla-carousel-react'
import { MouseEvent, useMemo, useState } from 'react'

import SvgIcon from '@/components/svg-icon/svgIcon'
import { Tag } from '@/components/tag/tag'
import { Typography } from '@/components/typography/typography'
import { cn } from '@/libs/tw'
import { CartService } from '@/stores/cart-store/slices/types'
import { translate } from '@/utils/translator'

import { useDotButton } from '../../hooks/useDots'
import { useRoomContext } from '../../hooks/useRoomContext'
import { RoomOffer, Rate as TRate } from '../../types'
import { RoomFeatures } from '../RoomFeatures'
import { RecommendedFlag } from '../recommendedFlag'
import { RoomDetails } from '../roomDetails'
import { RoomFeatureTags } from '../roomFeatureTags'
import { Rate } from './rate'

export type RoomProps = {
	room: RoomOffer
	selectedRate?: string
	selectedServices: { id: string; quantity: number }[]
	onSelect: (roomOffer: RoomOffer, rate: TRate, services?: CartService[]) => void
	onSelectRate: (rateCode?: string) => void
	onSelectService: (serviceCode: string, quantity?: number) => void
}

const sortRatesByPrice = (rates: TRate[]) =>
	rates.sort((a, b) => a.price.amountAfterTax - b.price.amountAfterTax)

const sortRates = (rates: TRate[], suggestedRateCode?: string) => {
	if (!rates || !suggestedRateCode) return sortRatesByPrice(rates) ?? []

	const suggestedRate = rates.find((rate) => rate.ratePlanCode === suggestedRateCode)
	if (!suggestedRate) return sortRatesByPrice(rates)

	return [
		suggestedRate,
		...sortRatesByPrice(rates).filter((rate) => rate.ratePlanCode !== suggestedRateCode),
	]
}

export const Room = ({ room, ...rest }: RoomProps) => {
	const [open, setOpen] = useState(false)
	const { roomConfig, roomNumber } = useRoomContext()
	const [roomDetails, setRoomDetails] = useState<RoomOffer>()

	const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true })
	const { selectedIndex, scrollSnaps, onDotButtonClick } = useDotButton(emblaApi)

	const handleImageClick = (e: MouseEvent<HTMLElement>) => {
		const rect = e.currentTarget.getBoundingClientRect()

		const middle = rect.left + rect.width / 2

		if (e.clientX < middle) return emblaApi?.scrollPrev()

		emblaApi?.scrollNext()
	}

	const showUnitsAvailable =
		room.numberOfUnitsAvailable !== 0 &&
		room.numberOfUnitsAvailable / room.numberOfUnitsTotal < 0.333 &&
		room.numberOfUnitsAvailable < 7

	const roomNotAvailable = room.numberOfUnitsAvailable === 0
	const isRecommended = room.roomTypeCode === roomConfig.roomSuggestion?.roomTypeCode

	const rates = useMemo(
		() => sortRates(room.rates, roomConfig.roomSuggestion?.ratePlanCode),
		[room, roomConfig]
	)

	return (
		<>
			<RoomDetails
				room={roomDetails}
				roomNumber={roomNumber}
				onClose={() => setRoomDetails(undefined)}
			/>
			<article
				className={cn(
					'relative flex flex-col gap-16 border-t border-textColor pt-24 text-textColor md:rounded-8 md:border-none md:bg-naturel md:p-24 md:pb-32 md:text-roomSelection-textColor',
					roomNotAvailable && 'pt-[106px] md:bg-primary md:text-naturel'
				)}
			>
				{isRecommended && (
					<div className={cn('absolute right-0 top-0 z-10 w-24 md:hidden')}>
						<RecommendedFlag />
					</div>
				)}

				{(!room.occupancyMatches || roomNotAvailable) && (
					<div
						className={cn(
							'absolute inset-0 z-30 flex h-full w-full justify-center rounded-8 bg-secondaryTransparent pt-32 text-roomSelection-notAvailableText md:pt-[126px]'
						)}
					>
						<Typography element="p" style="p" className={cn('md:hidden')}>
							{translate(
								!room.occupancyMatches
									? 'room-selection-occupancy-mismatch'
									: 'room-selection-not-available'
							)}
						</Typography>
						<Typography
							element="p"
							style="h3"
							className={cn('hidden justify-center px-[180px] text-center md:flex')}
						>
							{translate(
								!room.occupancyMatches
									? 'room-selection-occupancy-mismatch'
									: 'room-selection-not-available'
							)}
						</Typography>
					</div>
				)}
				<div className={cn('relative hidden h-[296px] rounded-8 md:block')}>
					{isRecommended && (
						<div className={cn('absolute left-16 top-0 z-10 w-24')}>
							<RecommendedFlag />
						</div>
					)}

					<div
						className={cn(
							'absolute left-12 top-12 z-10 flex w-[calc(100%-24px)] justify-end'
						)}
					>
						{showUnitsAvailable && (
							<Tag type="danger">
								<Typography element="span" style="p-s-bold">
									{translate('room-selection-available-rooms', [
										room.numberOfUnitsAvailable,
									])}
								</Typography>
							</Tag>
						)}
					</div>
					<section className={cn('relative hidden h-[296px] w-full rounded-8 md:block')}>
						<div
							className={cn(
								'overflow-hidden rounded-8',
								(!room.occupancyMatches || roomNotAvailable) && 'blur-[2px]'
							)}
							ref={emblaRef}
						>
							<div className={cn('flex touch-pan-y')}>
								{(!room.occupancyMatches || roomNotAvailable
									? room.images.slice(0, 1)
									: room.images
								).map((img) => (
									<img
										className={cn(
											'h-[296px] min-w-0 flex-[0_0_100%] object-cover'
										)}
										key={img.title}
										src={img.largeUrl}
										alt={img.alt}
										title={img.title}
										onClick={handleImageClick}
									/>
								))}
							</div>
						</div>
					</section>
					<div className={cn('absolute bottom-12 flex w-full justify-center gap-32')}>
						{scrollSnaps.map((_, index) => (
							<button
								aria-hidden
								tabIndex={-1}
								key={`carousel-dot-${index}`}
								onClick={() => onDotButtonClick(index)}
								className={cn(
									'h-10 w-10 rounded-[100%] bg-carousel-dot',
									index === selectedIndex && 'bg-carousel-dotActive'
								)}
							/>
						))}
					</div>
				</div>
				<Collapsible.Root
					open={open}
					onOpenChange={setOpen}
					className={cn('flex flex-col gap-24')}
				>
					<div className={cn('hidden flex-col justify-between gap-24 md:flex')}>
						<Typography
							element="h3"
							style="h3"
							className={cn(
								(!room.occupancyMatches || roomNotAvailable) &&
									'text-roomSelection-notAvailableText'
							)}
						>
							{room.title}
						</Typography>
						<div className={cn('flex justify-between gap-48')}>
							<RoomFeatureTags highlights={room.highlights} showAll={open} />
							<Collapsible.Trigger asChild className={cn('group')}>
								<button className={cn('flex gap-12')}>
									<Typography element="span" style="p">
										{translate('room-selection-details')}
									</Typography>
									<SvgIcon
										collectionName="ibe3"
										iconName="chevron"
										className="h-24 w-24 transition-transform duration-300 ease-[cubic-bezier(0.87,_0,_0.13,_1)] group-data-[state=closed]:rotate-180"
									/>
								</button>
							</Collapsible.Trigger>
						</div>
					</div>
					<div className={cn('flex items-center gap-16 md:hidden')}>
						<img
							className={cn('h-[104px] w-[104px] rounded-8 object-cover')}
							src={room.images[0].largeUrl}
							alt={room.images[0].alt}
							title={room.images[0].title}
						/>
						<div className={cn('flex flex-col gap-12')}>
							<Typography
								element="h3"
								style="p"
								className={cn(isRecommended && 'pr-[28px]')}
							>
								{room.title}
							</Typography>
							<div>
								<RoomFeatureTags highlights={room.highlights} />
							</div>
							{showUnitsAvailable && (
								<Typography element="p" style="p-s-bold">
									{translate('room-selection-available-rooms', [
										room.numberOfUnitsAvailable,
									])}
								</Typography>
							)}
							<button
								className={cn(
									'flex w-fit items-center gap-12 rounded-8 border border-textColor px-16 py-12'
								)}
								onClick={() => setRoomDetails(room)}
							>
								<Typography element="p" style="p-ms">
									{translate('room-selection-details')}
								</Typography>
								<SvgIcon
									collectionName="ibe3"
									iconName="arrow-right"
									className="h-16 w-16"
								/>
							</button>
						</div>
					</div>
					<Collapsible.Content className="overflow-hidden data-[state=closed]:animate-slideUpC data-[state=open]:animate-slideDownC">
						<RoomFeatures features={room.amenities} description={room.description} />
					</Collapsible.Content>
				</Collapsible.Root>
				<div
					className={cn(
						'flex flex-col border-textColor md:border-t md:border-dark-strong',
						open && 'mt-16'
					)}
				>
					{rates.map((rate) => (
						<Rate key={rate.ratePlanCode} room={room} rate={rate} {...rest} />
					))}
				</div>
			</article>
		</>
	)
}
